import { type PartnerStatus } from '@/api/routes/partners/partners.api';
import {
  type PartnerCampaign,
  type PartnerCampaignStatus,
} from '@/api/routes/partners/partners.campaigns.api';
import {
  type PartnerOffer,
  type PartnerOfferAction,
  type PartnerOfferActionKind,
  type PartnerOfferBody,
  type PartnerOfferLocation,
  type PartnerOfferLocationBody,
  type PartnerOfferStatus,
  type PartnerOffersListParams,
} from '@/api/routes/partners/partners.offers.api';
import { type ChipProps } from '@/components/Chip';
import {
  type OfferActionFormValues,
  type OfferCouponHint,
  type OfferFormSaveMode,
  type OfferFormValues,
  type OfferLocationFormValues,
  type OfferPreset,
  type OfferPresetShape,
  type OfferStructureValues,
  type OffersTab,
  type PartnerOfferActions,
} from '@/domain/offers/types';
import { getActiveLocale } from '@/i18n/activeLocale';
import { translateFile, translatedMap } from '@/i18n';

export const OFFERS_PAGE_SIZE = 20;

/** Limits of the API validation (`campaigns.validations.ts`, offer fields). */
export const OFFER_LIMITS = {
  titleMin: 3,
  titleMax: 60,
  bodyMin: 10,
  bodyMax: 240,
  actionLabelMin: 2,
  actionLabelMax: 30,
  actionsMax: 2,
  urlMax: 500,
  whatsappMessageMax: 200,
  couponMax: 30,
  couponTermsMax: 200,
  locationsMax: 20,
  locationLabelMax: 60,
  audienceMax: 280,
} as const;

export const OFFERS_TABS: OffersTab[] = [
  'all',
  'draft',
  'in_review',
  'changes_requested',
  'approved',
  'rejected',
  'archived',
];

export const offersTabLabel = translatedMap('domain.offers.utils', {
  all: 'tabs.all',
  draft: 'tabs.draft',
  in_review: 'tabs.in_review',
  changes_requested: 'tabs.changes_requested',
  approved: 'tabs.approved',
  rejected: 'tabs.rejected',
  archived: 'tabs.archived',
} as const);

export const offerStatusLabel = translatedMap('domain.offers.utils', {
  draft: 'status.draft',
  in_review: 'status.in_review',
  changes_requested: 'status.changes_requested',
  approved: 'status.approved',
  rejected: 'status.rejected',
  archived: 'status.archived',
} as const);

export const OFFER_STATUS_CHIP: Record<PartnerOfferStatus, Pick<ChipProps, 'variant' | 'color'>> = {
  draft: { variant: 'outline', color: 'neutral' },
  in_review: { variant: 'solid', color: 'warning' },
  changes_requested: { variant: 'solid', color: 'warning' },
  approved: { variant: 'solid', color: 'success' },
  rejected: { variant: 'solid', color: 'error' },
  archived: { variant: 'outline', color: 'neutral' },
};

/** Order of the "Adicionar botão" menu. */
export const OFFER_ACTION_KINDS: PartnerOfferActionKind[] = [
  'link',
  'whatsapp',
  'phone',
  'lead',
  'directions',
];

/** Kinds an offer can have only once (API: "Esse botão já está na oferta"). */
export const OFFER_SINGLE_ACTION_KINDS: PartnerOfferActionKind[] = ['lead', 'directions'];

/** What each kind is, in the "Adicionar botão" menu and on the button card. */
export const offerActionKindLabel = translatedMap('domain.offers.utils', {
  link: 'actionKind.link',
  whatsapp: 'actionKind.whatsapp',
  phone: 'actionKind.phone',
  lead: 'actionKind.lead',
  directions: 'actionKind.directions',
} as const);

/** One line under each kind in the menu. */
export const offerActionKindHint = translatedMap('domain.offers.utils', {
  link: 'actionHint.link',
  whatsapp: 'actionHint.whatsapp',
  phone: 'actionHint.phone',
  lead: 'actionHint.lead',
  directions: 'actionHint.directions',
} as const);

/** The button text when the partner leaves "Texto do botão" empty (same as the app). */
export const offerActionDefaultLabel = translatedMap('domain.offers.utils', {
  link: 'actionDefault.link',
  whatsapp: 'actionDefault.whatsapp',
  phone: 'actionDefault.phone',
  lead: 'actionDefault.lead',
  directions: 'actionDefault.directions',
} as const);

/** Short names for the offers table ("Cupom · Site"). */
const offerActionShortLabel = translatedMap('domain.offers.utils', {
  link: 'actionShort.link',
  whatsapp: 'actionShort.whatsapp',
  phone: 'actionShort.phone',
  lead: 'actionShort.lead',
  directions: 'actionShort.directions',
} as const);

export const isOfferActionKind = (value: string): value is PartnerOfferActionKind =>
  (OFFER_ACTION_KINDS as string[]).includes(value);

/** The text the button shows: the partner's, or the kind's default. */
export const getOfferActionLabel = (action: {
  kind: PartnerOfferActionKind;
  label?: string | null;
}) => action.label?.trim() || offerActionDefaultLabel[action.kind];

/** Order of the quick-start chips. */
export const OFFER_PRESETS: OfferPreset[] = [
  'onlineCoupon',
  'storeCoupon',
  'whatsappBooking',
  'leads',
];

const OFFER_PRESET_SHAPES: Record<OfferPreset, OfferPresetShape> = {
  onlineCoupon: { coupon: true, actions: ['link'] },
  storeCoupon: { coupon: true, actions: ['directions'] },
  whatsappBooking: { coupon: false, actions: ['whatsapp', 'directions'] },
  leads: { coupon: false, actions: ['lead'] },
};

export const offerPresetLabel = translatedMap('domain.offers.utils', {
  onlineCoupon: 'preset.onlineCoupon',
  storeCoupon: 'preset.storeCoupon',
  whatsappBooking: 'preset.whatsappBooking',
  leads: 'preset.leads',
} as const);

export const campaignStatusLabel = translatedMap('domain.offers.utils', {
  approved: 'campaign.approved',
  live: 'campaign.live',
  paused: 'campaign.paused',
  ended: 'campaign.ended',
} as const);

/** The tab in the URL (`?status=`), falling back to every offer for anything unknown. */
export const parseOffersTab = (value: string | null): OffersTab =>
  OFFERS_TABS.find((tab) => tab === value) ?? 'all';

export const toOffersListParams = (tab: OffersTab, page: number): PartnerOffersListParams => ({
  ...(tab === 'all' ? {} : { status: tab }),
  offset: page * OFFERS_PAGE_SIZE,
  limit: OFFERS_PAGE_SIZE,
});

const PARTNER_EDITABLE: PartnerOfferStatus[] = ['draft', 'changes_requested', 'rejected'];
const SUBMITTABLE: PartnerOfferStatus[] = ['draft', 'changes_requested'];

/** What the partner can do with an offer (contract: Ofertas). Sending needs an approved account. */
export const getPartnerOfferActions = (
  offer: Pick<PartnerOffer, 'status'>,
  partnerStatus: PartnerStatus,
): PartnerOfferActions => {
  const submittable = SUBMITTABLE.includes(offer.status);
  return {
    edit: PARTNER_EDITABLE.includes(offer.status),
    submit: submittable && partnerStatus === 'approved',
    submitLocked: submittable && partnerStatus !== 'approved',
    archive: offer.status !== 'archived',
  };
};

export const isOfferEditable = (offer: Pick<PartnerOffer, 'status'>) =>
  PARTNER_EDITABLE.includes(offer.status);

export const getOfferFormSaveMode = (
  offer?: Pick<PartnerOffer, 'status'> | null,
): OfferFormSaveMode => (offer?.status === 'changes_requested' ? 'changes_requested' : 'draft');

/** "Salvar e enviar" right from the form needs an approved account. */
export const canSubmitOfferFromForm = (partnerStatus: PartnerStatus) =>
  partnerStatus === 'approved';

export const emptyOfferAction = (kind: PartnerOfferActionKind): OfferActionFormValues => ({
  kind,
  label: '',
  url: '',
  number: '',
  message: '',
});

export const emptyOfferLocation = (): OfferLocationFormValues => ({
  label: '',
  postalCode: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
});

/** Up to 2 buttons, and `lead`/`directions` only once. */
export const canAddOfferAction = (
  actions: Pick<OfferActionFormValues, 'kind'>[],
  kind?: PartnerOfferActionKind,
): boolean => {
  if (actions.length >= OFFER_LIMITS.actionsMax) return false;
  if (!kind || !OFFER_SINGLE_ACTION_KINDS.includes(kind)) return true;
  return !actions.some((action) => action.kind === kind);
};

/** "Como chegar" opens the map to the offer's place, so "Onde vale" needs one. */
export const needsOfferLocation = (actions: Pick<OfferActionFormValues, 'kind'>[]): boolean =>
  actions.some((action) => action.kind === 'directions');

/** A place with nothing typed (the one a quick start or "Como chegar" adds). */
export const isBlankOfferLocation = (location: OfferLocationFormValues): boolean =>
  Object.values(location).every((value) => !value.trim());

/**
 * The places after the buttons change: "Como chegar" gets one empty place when there is none;
 * without it, places left blank go away so they do not block saving.
 */
export const fitOfferLocations = (
  locations: OfferLocationFormValues[],
  actions: Pick<OfferActionFormValues, 'kind'>[],
): OfferLocationFormValues[] => {
  if (needsOfferLocation(actions)) return locations.length ? locations : [emptyOfferLocation()];
  return locations.some(isBlankOfferLocation)
    ? locations.filter((location) => !isBlankOfferLocation(location))
    : locations;
};

/**
 * Writes a quick start's structure: coupon on or off (the code is kept), its buttons in order
 * (reusing what was typed in a button of the same kind) and the places that fit them.
 */
export const applyOfferPreset = (
  values: OfferStructureValues,
  preset: OfferPreset,
): OfferStructureValues => {
  const shape = OFFER_PRESET_SHAPES[preset];
  const actions = shape.actions.map(
    (kind) => values.actions.find((action) => action.kind === kind) ?? emptyOfferAction(kind),
  );
  return {
    benefit: { ...values.benefit, enabled: shape.coupon },
    actions,
    locations: fitOfferLocations(values.locations, actions),
  };
};

/** The quick start whose structure the form has now, so its chip reads as selected. */
export const getOfferPreset = (
  values: Pick<OfferStructureValues, 'actions'> & {
    benefit: Pick<OfferFormValues['benefit'], 'enabled'>;
  },
): OfferPreset | null =>
  OFFER_PRESETS.find((preset) => {
    const shape = OFFER_PRESET_SHAPES[preset];
    return (
      shape.coupon === values.benefit.enabled &&
      shape.actions.length === values.actions.length &&
      shape.actions.every((kind, index) => values.actions[index]?.kind === kind)
    );
  }) ?? null;

/** With a link the app copies the code and opens the store; without buttons it is shown at the counter. */
export const getOfferCouponHint = (
  actions: Pick<OfferActionFormValues, 'kind'>[],
): OfferCouponHint => {
  if (actions.some((action) => action.kind === 'link')) return 'withLink';
  if (!actions.length) return 'atCounter';
  return 'default';
};

/** The chat's first message when the partner leaves it empty (the API writes the same). */
export const getOfferWhatsappDefaultMessage = (title: string) =>
  translateFile('domain.offers.utils', 'whatsappDefaultMessage', {
    title: title.trim() || translateFile('domain.offers.utils', 'whatsappDefaultTitle'),
  });

export const emptyOfferFormValues = (): OfferFormValues => ({
  imageUrl: '',
  title: '',
  body: '',
  benefit: { enabled: false, code: '', terms: '' },
  actions: [],
  locations: [],
  desiredAudienceText: '',
  desiredStartsAt: '',
  desiredEndsAt: '',
});

const fromPartnerOfferAction = (action: PartnerOfferAction): OfferActionFormValues => ({
  ...emptyOfferAction(action.kind),
  label: action.label ?? '',
  url: action.kind === 'link' ? action.url : '',
  number: action.kind === 'whatsapp' || action.kind === 'phone' ? formatPhone(action.number) : '',
  message: action.kind === 'whatsapp' ? (action.message ?? '') : '',
});

const fromPartnerOfferLocation = (location: PartnerOfferLocation): OfferLocationFormValues => ({
  label: location.label ?? '',
  postalCode: location.postalCode ?? '',
  street: location.street,
  number: location.number ?? '',
  complement: location.complement ?? '',
  neighborhood: location.neighborhood ?? '',
  city: location.city,
  state: location.state,
});

export const fromPartnerOffer = (offer: PartnerOffer): OfferFormValues => ({
  imageUrl: offer.imageUrl ?? '',
  title: offer.title,
  body: offer.body,
  benefit: {
    enabled: Boolean(offer.benefit),
    code: offer.benefit?.code ?? '',
    terms: offer.benefit?.terms ?? '',
  },
  actions: offer.actions.map(fromPartnerOfferAction),
  locations: offer.locations.map(fromPartnerOfferLocation),
  desiredAudienceText: offer.desiredAudienceText ?? '',
  desiredStartsAt: offer.desiredStartsAt?.slice(0, 10) ?? '',
  desiredEndsAt: offer.desiredEndsAt?.slice(0, 10) ?? '',
});

const textOrNull = (value: string) => value.trim() || null;
const onlyDigits = (value: string) => value.replace(/\D/g, '');

/** Only the fields of the kind, trimmed; numbers with digits only. */
const toPartnerOfferAction = (action: OfferActionFormValues): PartnerOfferAction => {
  const label = textOrNull(action.label);
  switch (action.kind) {
    case 'link':
      return { kind: 'link', url: action.url.trim(), label };
    case 'whatsapp':
      return {
        kind: 'whatsapp',
        number: onlyDigits(action.number),
        message: textOrNull(action.message),
        label,
      };
    case 'phone':
      return { kind: 'phone', number: onlyDigits(action.number), label };
    case 'lead':
      return { kind: 'lead', label };
    case 'directions':
      return { kind: 'directions', label };
  }
};

const toPartnerOfferLocation = (location: OfferLocationFormValues): PartnerOfferLocationBody => ({
  label: textOrNull(location.label),
  street: location.street.trim(),
  number: textOrNull(location.number),
  complement: textOrNull(location.complement),
  neighborhood: textOrNull(location.neighborhood),
  city: location.city.trim(),
  state: location.state,
  country: 'BR',
  postalCode: textOrNull(location.postalCode),
});

/**
 * The coupon (and its rules) goes only with the switch on; the API replaces the whole list of
 * locations.
 */
export const toPartnerOfferBody = (values: OfferFormValues): PartnerOfferBody => {
  const code = values.benefit.enabled ? values.benefit.code.trim() : '';
  const terms = textOrNull(values.benefit.terms);
  return {
    title: values.title.trim(),
    body: values.body.trim(),
    imageUrl: values.imageUrl || null,
    benefit: code ? { kind: 'coupon', code, terms } : null,
    actions: values.actions.map(toPartnerOfferAction),
    locations: values.locations.map(toPartnerOfferLocation),
    desiredAudienceText: textOrNull(values.desiredAudienceText),
    desiredStartsAt: values.desiredStartsAt || null,
    desiredEndsAt: values.desiredEndsAt || null,
  };
};

const OFFER_FORM_FIELDS = new Set<string>([
  ...Object.keys(emptyOfferFormValues()),
  'benefit.code',
  'benefit.terms',
]);
const OFFER_ACTION_FIELD = /^actions\.\d+\.(kind|label|url|number|message)$/;
const OFFER_LOCATION_FIELD = new RegExp(
  `^locations\\.\\d+\\.(${Object.keys(emptyOfferLocation()).join('|')})$`,
);

/** API field errors on these paths land under their input; the rest go to the top alert. */
export const isOfferFormVisibleField = (path: string) =>
  OFFER_FORM_FIELDS.has(path) || OFFER_ACTION_FIELD.test(path) || OFFER_LOCATION_FIELD.test(path);

/** "Rua X, 123 - Bairro, Cidade/UF". */
export function formatOfferLocationAddress(
  location: Pick<OfferLocationFormValues, 'street' | 'city' | 'state'> & {
    number?: string | null;
    neighborhood?: string | null;
  },
): string {
  const street = [location.street.trim(), location.number?.trim()].filter(Boolean).join(', ');
  const place = [street, location.neighborhood?.trim()].filter(Boolean).join(' - ');
  const city = [location.city.trim(), location.state.trim()].filter(Boolean).join('/');
  return [place, city].filter(Boolean).join(', ');
}

/** "Cupom · Site", "WhatsApp · Como chegar"; a dash for an offer with neither. */
export function offerStructureSummary(offer: Pick<PartnerOffer, 'benefit' | 'actions'>): string {
  const parts = [
    ...(offer.benefit ? [translateFile('domain.offers.utils', 'coupon')] : []),
    ...offer.actions.map((action) => offerActionShortLabel[action.kind]),
  ];
  return parts.length ? parts.join(' · ') : '—';
}

/** Where a button leads, for the detail page: the link or the formatted number. */
export function getOfferActionTarget(action: PartnerOfferAction): string | null {
  if (action.kind === 'link') return action.url;
  if (action.kind === 'whatsapp' || action.kind === 'phone') return formatPhone(action.number);
  return null;
}

/** A civil date (`YYYY-MM-DD…`) in local time, so the day never shifts with the timezone. */
const toCivilDate = (date: string) => {
  const [year, month, day] = date.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

/** "16 out". */
export const formatOfferDay = (date: string): string =>
  new Intl.DateTimeFormat(getActiveLocale(), { day: 'numeric', month: 'short' })
    .format(toCivilDate(date))
    .replace('.', '')
    .replace(' de ', ' ');

/** "1 out – 30 nov", "A partir de 1 out", "Até 30 nov" or null without a preference. */
export function formatOfferPeriod(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
): string | null {
  if (startsAt && endsAt) return `${formatOfferDay(startsAt)} – ${formatOfferDay(endsAt)}`;
  if (startsAt)
    return translateFile('domain.offers.utils', 'period.from', {
      date: formatOfferDay(startsAt),
    });
  if (endsAt)
    return translateFile('domain.offers.utils', 'period.until', {
      date: formatOfferDay(endsAt),
    });
  return null;
}

/** Most telling campaign first: on air, then paused, approved, ended. */
const CAMPAIGN_PRIORITY: PartnerCampaignStatus[] = ['live', 'paused', 'approved', 'ended'];

/** The campaign of each offer that tells the most about it ("No ar até 30 nov"). */
export const getCampaignByOffer = (campaigns: PartnerCampaign[]): Map<string, PartnerCampaign> => {
  const byOffer = new Map<string, PartnerCampaign>();
  for (const campaign of campaigns) {
    const current = byOffer.get(campaign.offer.id);
    if (
      !current ||
      CAMPAIGN_PRIORITY.indexOf(campaign.status) < CAMPAIGN_PRIORITY.indexOf(current.status)
    ) {
      byOffer.set(campaign.offer.id, campaign);
    }
  }
  return byOffer;
};

/** "No ar até 30 nov", "Pausada", "Aprovada · começa 1 dez", "Encerrada". */
export function campaignSummaryLabel(
  campaign: Pick<PartnerCampaign, 'status' | 'startsAt' | 'endsAt'>,
): string {
  if (campaign.status === 'live') {
    return campaign.endsAt
      ? translateFile('domain.offers.utils', 'campaignSummary.liveUntil', {
          date: formatOfferDay(campaign.endsAt),
        })
      : campaignStatusLabel.live;
  }
  if (campaign.status === 'approved') {
    return translateFile('domain.offers.utils', 'campaignSummary.startsAt', {
      date: formatOfferDay(campaign.startsAt),
    });
  }
  return campaignStatusLabel[campaign.status];
}

/** A phone or WhatsApp as "(83) 99999-0000" or "0800 123 4567"; anything else comes back as typed. */
export function formatPhone(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.startsWith('0')) {
    return digits.length === 11 && digits.startsWith('0800')
      ? `0800 ${digits.slice(4, 7)} ${digits.slice(7)}`
      : (value ?? '');
  }
  const local = digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return value ?? '';
}
